import {
  Button,
  ButtonProps,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { MenuItemProps, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import {
  useDeleteEmailCycleStore,
  useEmailCycleDetailsStore,
  useUpdateEmailCycleStore,
} from "../../stores/Hooks";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { UpdateEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateEmailCycleRPC";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { DeleteEmailCycleDialog } from "../dialogs/DeleteEmailCycleDialog";
import { MenuButton } from "../../../../components/buttons/MenuButton";
import { DeleteEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/deleteEmailCycleRPC";

interface EmailCycleDetailsActionButtonsProps {
  fetchEmailCycleDetails: VoidFunction;
}

type DialogToOpen =
  | "SUCCESS"
  | "CYCLE_ALREADY_LAUNCH_ERROR"
  | "UNEXPECTED_ERROR"
  | "DELETE_EMAIL_CYCLE"
  | "INVALID_SCHEDULE";

const DIVIDER_HEIGHT = "24px";

export const EmailCycleDetailsActionButtons: React.FC<EmailCycleDetailsActionButtonsProps> =
  observer(({ fetchEmailCycleDetails }) => {
    const [dialogToOpen, setDialogToOpen] = useState<DialogToOpen>();

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const navigateTo = useRouteNavigator();

    const emailCycleDetailsStore = useEmailCycleDetailsStore();
    const deleteEmailCycleStore = useDeleteEmailCycleStore();
    const updateEmailCycleStore = useUpdateEmailCycleStore();
    const isImmediateSchedule =
      emailCycleDetailsStore.scheduleType.type === "IMMEDIATE_SCHEDULE";

    const openDeleteEmailCycleDialog = (
      scope: DeleteEmailCycleRPC.RequestEnums.Scope.Scope,
    ): void => {
      deleteEmailCycleStore.setCycleId(emailCycleDetailsStore.scheduleId);
      deleteEmailCycleStore.emailCycleName.setName(
        emailCycleDetailsStore.displayCycleName.name,
      );
      deleteEmailCycleStore.setScope(scope);
      setDialogToOpen("DELETE_EMAIL_CYCLE");
    };

    const deleteCycleMenuOptions: MenuItemProps[] = [
      {
        children: t("emailCycles.deleteThisCycleLabel"),
        onClick: (): void =>
          openDeleteEmailCycleDialog(
            DeleteEmailCycleRPC.RequestEnums.Scope.Scope.ONLY_THIS,
          ),
      },
      {
        children: t("emailCycles.deleteThisAndAllPendingCyclesLabel"),
        onClick: (): void =>
          openDeleteEmailCycleDialog(
            DeleteEmailCycleRPC.RequestEnums.Scope.Scope.THIS_AND_PENDING,
          ),
      },
    ];

    const deleteButtonProps: Omit<ButtonProps, "onClick"> = {
      label: t("emailCycles.deleteCycle"),
      name: "deleteCycleButton",
      variant: "outlined-color",
      size: "medium",
      color: "destructive",
      disabled: emailCycleDetailsStore.loadingEmailCycleDetails,
    };

    const handleSaveChanges = async (): Promise<void> => {
      await updateEmailCycleStore
        .updateEmailCycle()
        .then(() => {
          if (updateEmailCycleStore.disableSubmit) return;

          switch (updateEmailCycleStore.updateEmailCycleRPCError) {
            case null:
              setDialogToOpen("SUCCESS");
              break;
            case UpdateEmailCycleRPC.RPCError.ScheduleInProgress:
              setDialogToOpen("CYCLE_ALREADY_LAUNCH_ERROR");
              break;
            case UpdateEmailCycleRPC.RPCError.InvalidScheduleId:
              navigateTo.pageNotFound();
              break;
            case UpdateEmailCycleRPC.RPCError.ProjectAlreadyArchived:
              setDialogToOpen("UNEXPECTED_ERROR");
              break;
            case UpdateEmailCycleRPC.RPCError.InvalidSchedule:
              setDialogToOpen("INVALID_SCHEDULE");
              break;
            default:
              updateEmailCycleStore.updateEmailCycleRPCError satisfies never;
          }
        })
        .catch(() => setDialogToOpen("UNEXPECTED_ERROR"));
    };

    const handleDialogClose = (refetchDetails = true): void => {
      setDialogToOpen(undefined);
      if (refetchDetails) fetchEmailCycleDetails();
    };

    return (
      <>
        <Stack
          height={"100%"}
          direction="row"
          divider={
            <DividerComponent orientation="vertical" height={DIVIDER_HEIGHT} />
          }
          gap={spacing.spaceMD}
        >
          {emailCycleDetailsStore.scheduleType.type === "RECURRING_SCHEDULE" ? (
            <MenuButton
              button={deleteButtonProps}
              menuItems={deleteCycleMenuOptions.map(
                ({ children, ...menuItemProps }) => ({
                  children: (
                    <Typography {...typography.b1}>{children}</Typography>
                  ),
                  ...menuItemProps,
                }),
              )}
            />
          ) : (
            <Button
              {...deleteButtonProps}
              onClick={() =>
                openDeleteEmailCycleDialog(
                  DeleteEmailCycleRPC.RequestEnums.Scope.Scope.ONLY_THIS,
                )
              }
            />
          )}
          <Button
            label={t("common.saveChangesButtonText")}
            name="saveEmailCycleChangesButton"
            variant="filled"
            size="medium"
            disabled={
              isImmediateSchedule ||
              updateEmailCycleStore.disableSubmit ||
              updateEmailCycleStore.loadingUpdateEmailCycle
            }
            onClick={handleSaveChanges}
          />
        </Stack>

        {dialogToOpen === "SUCCESS" && (
          <SuccessDialog
            title={t("emailCycles.updateEmailCycleSuccessDialogTitle")}
            message={t("emailCycles.updateEmailCycleSuccessDialogDescription")}
            onClickClose={handleDialogClose}
          />
        )}

        {dialogToOpen === "CYCLE_ALREADY_LAUNCH_ERROR" && (
          <ErrorDialog
            title={t("common.failedToSaveChanges")}
            message={t("emailCycles.emailCycleAlreadyLaunchErrorMessage")}
            onClickClose={handleDialogClose}
          />
        )}

        {dialogToOpen === "INVALID_SCHEDULE" && (
          <ErrorDialog
            title={t("emailCycles.invalidScheduleId")}
            message={t("emailCycles.invalidScheduleIdErrorMessage")}
            onClickClose={() => {
              updateEmailCycleStore.resetRPCError();
              setDialogToOpen(undefined);
            }}
          />
        )}

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t("emailCycles.updateEmailCycleUnexpectedErrorMessage")}
            onClickDone={handleDialogClose}
          />
        )}

        {dialogToOpen === "DELETE_EMAIL_CYCLE" && (
          <DeleteEmailCycleDialog handleDialogClose={handleDialogClose} />
        )}
      </>
    );
  });
